package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.Address
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimRole
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingDetailsEdit
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import kotlinx.coroutines.flow.Flow

/**
 * Read access to the directory. Implementations live behind this interface so the UI never touches
 * Firestore directly and can be driven by a fake in tests/previews. Queries scope by **metro** and
 * optionally **category** (see `docs/LOCALIZATION.md`); a null filter means "all".
 *
 * Streams emit [DataResult] so the UI can render loading/empty/error states; only `approved` listings
 * are returned (Firestore rules enforce this for non-owners).
 */
interface BusinessRepository {
    /** Signed-in uid (null for guests) — lets the UI hide owner-inapplicable actions (e.g. self-review). */
    val currentUid: String?

    /**
     * All approved businesses in [metroId], optionally filtered to [categoryId]. When [metroId] is null
     * ("All \<country\>") and [country] is a 2-letter code ("CA"/"US"), results are scoped to that
     * country via `address.country`; a selected metro already implies its country, so [country] is only
     * applied in the metro-null case. Both null = the whole directory (used by id-list hydration/tests).
     */
    fun observeBusinesses(
        metroId: String? = null,
        categoryId: String? = null,
        country: String? = null,
    ): Flow<DataResult<List<Business>>>

    /** Sponsored/featured businesses for the home carousel, scoped to [metroId] (null = "All [country]"). */
    fun observeFeatured(metroId: String? = null, country: String? = null): Flow<DataResult<List<Business>>>

    /**
     * The most recently approved listings, newest first, scoped like [observeBusinesses] ([metroId],
     * else [country]). Feeds the community feed's "New in your mahaleh" cards and the Suggested rail.
     */
    fun observeNewest(metroId: String? = null, country: String? = null, limit: Int = 12): Flow<DataResult<List<Business>>>

    /** A single business by id (e.g. for the detail screen). */
    suspend fun getBusiness(id: String): DataResult<Business>

    /**
     * Resolves a set of ids to businesses (parallel point reads; unknown/undecodable ids are
     * skipped). This is how id-lists (favorites, follows) hydrate — NEVER by filtering a streamed
     * corpus, which bills the whole directory to render a handful of rows.
     */
    suspend fun getBusinesses(ids: Collection<String>): List<Business>

    /**
     * Creates a new owner-submitted listing from the Add Business wizard: a `businesses/{id}` doc
     * that is **pending + unverified + owned by the caller** (rules pin all three; an admin approves
     * it into the public directory from the moderation queue). Returns the new document id. Requires
     * a real (non-anonymous) account — guests get
     * [diana.soleil.design.iraniancanadienbazzar.core.common.AppError.Unauthorized].
     */
    suspend fun submitBusiness(
        name: LocalizedText,
        categoryId: String,
        description: LocalizedText,
        addressLine: String,
        city: String,
        province: String,
        postalCode: String,
        metroId: String?,
        /** Enabled structured-fact keys for the picked category (see BusinessAttributeRegistry). */
        attributes: List<String> = emptyList(),
    ): DataResult<String>

    /**
     * Replaces the owner-declared structured facts on [businessId] (rules allow the owner to edit
     * `attributes`; see BusinessAttributeRegistry for the per-category catalog).
     */
    suspend fun updateBusinessAttributes(businessId: String, attributes: List<String>): DataResult<Unit>

    /**
     * Permanently deletes a listing. Owner-or-admin only (rules: `allow delete` gates on
     * `isAdmin() || ownsExisting('ownerUid')`). Irreversible — the caller confirms with the user
     * first. Related subcollections/media are not cascaded here (a Function reaps orphans server-side).
     */
    suspend fun deleteBusiness(businessId: String): DataResult<Unit>

    /**
     * Starts a paid-promotion purchase for [businessId] on the given [planId] (see the app's
     * PromotionPlan catalog / the `createPromotionCheckout` Function's PROMOTION_PLANS). Returns the
     * Stripe hosted-Checkout URL for the caller to open; the `stripeWebhook` Function (not the client)
     * activates the sponsorship after payment. Owner-only (the Function re-checks ownership).
     */
    suspend fun createPromotionCheckout(businessId: String, planId: String): DataResult<String>

    /**
     * Owner privacy controls: [hideExactAddress] suppresses the street line/pin (home-based listing),
     * [ownerHidden] removes the listing from public discovery. Owner/admin only (rules).
     */
    suspend fun updateBusinessPrivacy(
        businessId: String,
        hideExactAddress: Boolean,
        ownerHidden: Boolean,
    ): DataResult<Unit>

    /**
     * Uploads picked media for a listing the caller owns (Storage rules verify via Firestore lookup)
     * and stamps the download URLs onto the business doc: [cover]/[logo] REPLACE `coverUrl`/`logoUrl`,
     * [gallery] is APPENDED to `photos` (it used to overwrite the whole gallery, so an owner adding
     * one photo wiped the rest), and `mediaStatus` flips to `owner_media`. Uploads are best-effort per
     * image: whatever succeeded is stamped, and a [DataResult.Failure] return means at least one image
     * failed (the listing itself is unaffected). No-ops to Success when every argument is null/empty.
     */
    suspend fun uploadBusinessMedia(
        businessId: String,
        cover: PickedImage?,
        logo: PickedImage?,
        gallery: List<PickedImage>,
    ): DataResult<Unit>

    /**
     * Owner edit of the listing details that need no admin gate: service title, description, contact,
     * hours, languages, price level, chat/ordering. On an approved listing the BIZ-5 Function still
     * flags it for an after-the-fact re-review, but it stays live.
     */
    suspend fun updateListingDetails(businessId: String, edit: ListingDetailsEdit): DataResult<Unit>

    /** Removes one photo URL from the listing's gallery (`photos`); the owner's other photos stay. */
    suspend fun removeBusinessPhoto(businessId: String, photoUrl: String): DataResult<Unit>

    /**
     * Asks an admin to change [business]'s identity fields: [name], [categoryId] and/or [address]
     * (null = unchanged). Writes a pending `listingChangeRequests` doc carrying the current values
     * too; nothing on the listing changes until an admin approves. Approved listings reject these
     * edits when written directly (rules), so this is the only path for them.
     */
    suspend fun requestListingChange(
        business: Business,
        name: String?,
        categoryId: String?,
        address: Address?,
    ): DataResult<Unit>

    /** The signed-in owner's still-pending change requests for [businessId] (drives the editor banner). */
    fun observePendingListingChanges(businessId: String): Flow<DataResult<List<ListingChangeRequest>>>

    /**
     * Asks to claim an unclaimed listing: flips `claimStatus -> claim_requested` and records the
     * caller as `claimRequestedBy` (rules enforce both). An admin grants/denies from the admin queue.
     * Requires a real (non-anonymous) account.
     */
    suspend fun requestClaim(businessId: String): DataResult<Unit>

    /**
     * Submits a verification-backed ownership claim: writes a `claims/{claimId}` doc (the claimant's
     * uid is stamped server-side from the session, so [claimantName] is just the display name they
     * gave) and best-effort flips the business to `claim_requested` so the existing "claim requested"
     * UX/gates keep working. An admin reviews the claim and, on approval, maps ownership. Requires a
     * real (non-anonymous) account — guests get [diana.soleil.design.iraniancanadienbazzar.core.common.AppError.Unauthorized].
     *
     * [proof] is an optional ownership-proof photo (licence, bill, storefront). It is uploaded to the
     * private `claim-proofs/{uid}/` Storage path before the claim doc is written, and its download URL
     * lands on the claim (`documentRef`) for the admin queue. Upload failure fails the whole submit —
     * silently dropping the evidence the claimant chose to attach would be worse than a retry.
     */
    suspend fun submitClaim(
        businessId: String,
        businessName: String,
        claimantName: String,
        role: ClaimRole,
        contactEmail: String,
        contactPhone: String,
        message: String,
        proof: PickedImage? = null,
    ): DataResult<Unit>
}
